import { Box, IconButton, Paper } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import React, { useEffect, useRef, useState } from 'react';
import { CommonModal, CustomPagination, FunctionsEnum } from '@watter/shared-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { userEnums } from '@watter/shared-types';
import { AntSwitch, CommonTable } from '../../components/common-styles';
import { CustomSlider } from '../../components/slider/slider';
import { UPDATE_INSTALLER } from '../../graphql/mutations';
import { GET_MANAGE_INSTALLERS } from '../../graphql/queries';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { formatDateWithTimeWithSeconds } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { AddInstaller } from './addInstaller';
import { usePermissions } from '../../custom_hooks/permissions';
import { UpdateInstaller } from './update-installer';
import { InstallerData, InstallerRow } from './type';

declare global {
    interface Window {
        handleAddInstaller?: () => void;
    }
}

export const ManageInstaller = () => {
    const navigate = useNavigate();
    const [open, setOpen] = React.useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [manageInstallersData, setManageInstallersData] = useState<InstallerRow[]>([]);
    const [selectedInstaller, setSelectedInstaller] = useState<InstallerRow | null>(null);
    const [newStatus, setNewStatus] = useState<number | null>(null);
    const dispatch = useDispatch();
    const [totalCount, setTotalRecordCount] = React.useState(0);
    const [isEdit, setIsEdit] = React.useState(false);
    const rowsPerPage = 10;
    const [page, setCurrentPage] = React.useState(1);
    const tableRef = useRef<HTMLInputElement>(null);
    const handleEditClick = (installer: InstallerRow, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedInstaller(installer);
        setIsEdit(true);
        setOpen(true);
    };
    const { canEdit } = usePermissions();
    const handleStatusToggle = (row: InstallerRow) => {
        setSelectedInstaller(row);
        setNewStatus(row.status === userEnums.Status.active ? userEnums.Status.inactive : userEnums.Status.active);
        setModalOpen(true);
    };
    const formatInstallerRow = (installer: InstallerData): InstallerRow => ({
        id: installer.id,
        username: installer.username,
        license_number: installer.license_number || '-',
        phone_number: installer.phone_number || '-',
        email: installer.email || '-',
        last_logged_at: installer.last_logged_at ? formatDateWithTimeWithSeconds(installer.last_logged_at) : '-',
        last_installation_date: installer.last_installation_date
            ? formatDateWithTimeWithSeconds(installer.last_installation_date)
            : '-',
        count: installer.count ?? '-',
        avg_installation_time: installer.avg_installation_time ? `${installer.avg_installation_time} Hrs` : '-',
        status: installer.status,
        cognito_id: installer.cognito_id || '-',
    });

    const fetchMangeInstallerData = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query<{
                // eslint-disable-next-line no-undef
                manageInstallersList: {
                    // eslint-disable-next-line no-use-before-define
                    data: InstallerData[];
                    // eslint-disable-next-line no-undef
                    total_count: number;
                };
            }>({
                query: GET_MANAGE_INSTALLERS,
                variables: {
                    limit: rowsPerPage,
                    page,
                },
                fetchPolicy: 'network-only',
            });

            const installerList = data?.manageInstallersList?.data;
            if (installerList) {
                const formattedRows: InstallerRow[] = installerList.map(formatInstallerRow);
                setTotalRecordCount(data?.manageInstallersList?.total_count);
                setManageInstallersData(formattedRows);
                return;
            }
            console.warn('No data found in API response');
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, page, rowsPerPage]);
    const handleStatusToggleApi = async (cognitoId: string, status: number, id: number) => {
        try {
            dispatch(showLoader());

            const input = {
                cognito_id: cognitoId,
                status,
                user_id: id,
            };

            const { data } = await client.mutate({
                mutation: UPDATE_INSTALLER,
                variables: { input },
            });

            await fetchMangeInstallerData();

            dispatch(
                openToast({
                    show: true,
                    message: data?.updateInstaller?.message,
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
        } catch (err) {
            console.error('Error while changing the status:', err);
        } finally {
            dispatch(hideLoader());
        }
    };
    const handleConfirmStatusChange = async () => {
        if (!selectedInstaller || newStatus === null) return;

        await handleStatusToggleApi(selectedInstaller.cognito_id.toString(), newStatus, selectedInstaller.id);

        setModalOpen(false);
        setSelectedInstaller(null);
        setNewStatus(null);
    };
    const handleAddInstaller = () => {
        setIsEdit(false);
        setOpen(true);
    };
    useEffect(() => {
        window.handleAddInstaller = handleAddInstaller;

        return () => {
            delete window.handleAddInstaller;
        };
    }, []);
    const columns: GridColDef[] = [
        { field: 'username', headerName: 'Name', flex: 1.2 },
        { field: 'license_number', headerName: 'License No', flex: 1 },
        { field: 'last_installation_date', headerName: 'Last Installation Date', flex: 0.8 },
        { field: 'count', headerName: 'Total Installations', flex: 0.7 },
        { field: 'avg_installation_time', headerName: 'Average Installation Time', flex: 0.9 },
        ...(canEdit(FunctionsEnum.MANAGE_INSTALLERS)
            ? [
                  {
                      field: 'status',
                      headerName: 'Status',
                      sortable: false,
                      flex: 0.4,
                      headerAlign: 'center' as const,
                      align: 'center' as const,
                      cellClassName: canEdit(FunctionsEnum.MANAGE_INSTALLERS) ? 'cstRemovePointer' : '',
                      renderCell: (params: GridRenderCellParams) => {
                          const isActive = params.value === userEnums.Status.active;
                          return (
                              <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                                  <AntSwitch
                                      checked={isActive}
                                      disabled={!canEdit(FunctionsEnum.MANAGE_INSTALLERS)}
                                      onClick={e => {
                                          e.stopPropagation();
                                          handleStatusToggle(params.row);
                                      }}
                                      sx={{
                                          '&.Mui-disabled': {
                                              opacity: 0.5,
                                              cursor: 'not-allowed',
                                          },
                                      }}
                                  />
                              </Box>
                          );
                      },
                  },
              ]
            : []),
        ...(canEdit(FunctionsEnum.MANAGE_INSTALLERS)
            ? [
                  {
                      field: 'actions',
                      headerName: 'Actions',
                      sortable: false,
                      flex: 0.4,
                      headerAlign: 'right' as const,
                      align: 'right' as const,
                      cellClassName: canEdit(FunctionsEnum.MANAGE_INSTALLERS) ? 'cstRemovePointer' : '',
                      renderCell: (params: GridRenderCellParams) => (
                          <Box display="flex" justifyContent="end">
                              <IconButton
                                  disabled={!canEdit(FunctionsEnum.MANAGE_INSTALLERS)}
                                  onClick={e => handleEditClick(params.row, e)}
                              >
                                  {' '}
                                  <EditOutlinedIcon
                                      sx={{
                                          '&.Mui-disabled': {
                                              opacity: 0.5,
                                              cursor: 'not-allowed',
                                          },
                                      }}
                                  />
                              </IconButton>
                          </Box>
                      ),
                  },
              ]
            : []),
    ];
    const handleRowClick = (params: GridRowParams) => {
        const row = params.row as InstallerRow;
        navigate(NavigationConfig.InstallerDetails.replace(':id', row.id.toString()), {
            state: { installerData: row },
        });
    };
    React.useEffect(() => {
        setManageInstallersData([]);
        fetchMangeInstallerData();
        if (tableRef.current) {
            tableRef.current.scrollIntoView();
        }
    }, [page, fetchMangeInstallerData]);

    return (
        <CommonTable height="calc(100vh - 200px)">
            <Paper sx={{ width: '100%' }}>
                <DataGrid
                    rows={manageInstallersData}
                    columns={columns}
                    disableColumnMenu
                    disableColumnSorting
                    hideFooter
                    onRowClick={handleRowClick}
                    ref={tableRef}
                />
                {totalCount > 0 && (
                    <CustomPagination
                        totalCount={totalCount}
                        rowsPerPage={rowsPerPage}
                        currentPage={page}
                        onPageChange={setCurrentPage}
                    />
                )}
                <CustomSlider
                    sliderOpen={open}
                    sliderClose={() => setOpen(false)}
                    drawerList={
                        isEdit ? (
                            <UpdateInstaller
                                onClose={() => setOpen(false)}
                                installer={selectedInstaller}
                                fetchMangeInstallerData={fetchMangeInstallerData}
                                open={open}
                            />
                        ) : (
                            <AddInstaller
                                onClose={() => setOpen(false)}
                                fetchMangeInstallerData={fetchMangeInstallerData}
                                open={open}
                            />
                        )
                    }
                />
            </Paper>
            <CommonModal
                modalOpen={modalOpen}
                header={`Do you want to ${newStatus === userEnums.Status.active ? 'activate' : 'inactivate'} this installer?`}
                cancelBtnName="Cancel"
                AcceptBtnName="Yes"
                closePopup={() => setModalOpen(false)}
                acceptBtn={handleConfirmStatusChange}
            />
        </CommonTable>
    );
};
